import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { authService } from '@/lib/authService';
import { roundMoney, multiplyMoney } from '@/lib/money';
import { getPreferredDefaultTaxRate, parseTaxRateValue } from '@/lib/finance/taxRates';
import { useStockReservations } from '@/hooks/useStockReservations';
import { useCustomerPricing } from '@/hooks/useCustomerPricing';
import type { StockAvailabilityCheck as StockAvailabilityCheckType } from '@/lib/inventory/stockReservationService';
import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import type { DocumentTaxRate, DocumentLineItemValue } from '../SortableLineItem';
import type { ExpenseAccountOption, AllocationType } from '@/lib/finance/expenseAccountMapping';
import { getDerivedAllocationType, getPreferredAllocationType } from '@/lib/finance/expenseAccountMapping';
import { useAllocationDimensions, type LineItemAllocation } from '@/hooks/useAllocationDimensions';
import type { SplitAllocation } from '@/components/shared/finance/SplitAllocationEditor';
import { useSemanticIntake } from '@/hooks/useSemanticIntake';
import type { InventoryItemMatch } from '@/lib/finance/matching/aiMatcher';
import type { FinanceItem, ItemSelectionOptions, PurchaseLineSourcingDetails } from '../ItemSearchModal';
import type { SelectedBillableItem } from '../SourceReferencesModal';
import type { InsertMenuPlacement } from '../documentModalHelpers';
import type { DocumentFormState } from '../documentModalHelpers';
import type { Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import type { Document } from '@/hooks/useDocumentsWorkItems';

interface UseLineItemsParams {
  isOpen: boolean;
  doc_type: string;
  document: Document | null | undefined;
  formData: DocumentFormState;
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  taxRates: DocumentTaxRate[];
  settingsDefaultTaxRateId: number | null;
  selectedContact: Contact | null;
  expenseAccounts: ExpenseAccountOption[];
  isDarkMode: boolean;
  currencySymbol: string;
  formatCurrency: (val: number) => string;
  documentInitializationKey: string;
  items: DocumentLineItem[];
  setItems: React.Dispatch<React.SetStateAction<DocumentLineItem[]>>;
  clientId?: number;
}

export function useLineItems({
  isOpen,
  doc_type,
  document: doc,
  formData,
  setFormData,
  taxRates,
  settingsDefaultTaxRateId,
  selectedContact,
  expenseAccounts,
  isDarkMode,
  currencySymbol,
  formatCurrency,
  documentInitializationKey,
  items,
  setItems,
  clientId,
}: UseLineItemsParams) {
  const { checkBulkAvailability } = useStockReservations();
  const { autoApplyCustomerPricing } = useCustomerPricing();
  const { dimensions: allocationDimensions } = useAllocationDimensions(clientId);
  // Semantic intake for historical GL allocations (powers full-chain AI suggestions)
  const isExpenseLikeDoc = doc_type === 'expense' || doc_type === 'supplier_invoice' || doc_type === 'din';
  const semanticIntake = useSemanticIntake({ enabled: isOpen && isExpenseLikeDoc });

  const [showItemSearch, setShowItemSearch] = useState(false);
  const [openInsertMenu, setOpenInsertMenu] = useState<InsertMenuPlacement | null>(null);
  const [highlightedInsertIndex, setHighlightedInsertIndex] = useState(-1);
  const [selectedItemIndices, setSelectedItemIndices] = useState<number[]>([]);
  const [inlineSearchIndex, setInlineSearchIndex] = useState<number | null>(null);
  const [splitIdx, setSplitIdx] = useState<number | null>(null);
  const [splitQty, setSplitQty] = useState(1);
  const [expandedAllocations, setExpandedAllocations] = useState<Set<number>>(new Set());

  // GL allocation mode: 'single' (one GL for all line items) or 'per_line' (each line has its own)
  // Default to 'single' for expense-like documents; 'per_line' for everything else.
  const [glAllocationMode, setGlAllocationMode] = useState<'single' | 'per_line'>(
    isExpenseLikeDoc ? 'single' : 'per_line'
  );

  // Whether the source document is vehicle-linked (fuel/toll DIN captured
  // against a vehicle). When true, GL accounts that allow 'vehicle'
  // allocation should default to it so the captured vehicle link is not
  // silently dropped by the account's first allowed type ('overhead').
  const docCustomFieldsForVehicle = (doc?.custom_fields ?? {}) as Record<string, unknown>;
  const docHasVehicleLink = Boolean(
    doc?.vehicle_id || doc?.vehicle_registration
    || docCustomFieldsForVehicle.vehicle_id || docCustomFieldsForVehicle.vehicle_registration,
  );

  // Stock availability state
  const [stockAvailability, setStockAvailability] = useState<Map<string, StockAvailabilityCheckType[]>>(new Map());
  const [showStockAvailability, setShowStockAvailability] = useState(false);
  const [checkingStock, setCheckingStock] = useState(false);
  const [showBackorderWorkflow, setShowBackorderWorkflow] = useState(false);
  const [insufficientStockItems, setInsufficientStockItems] = useState<Array<{
    work_item_id: string;
    item_name?: string;
    item_code?: string;
    availability: StockAvailabilityCheckType;
  }>>([]);

  // Customer pricing state
  const [customerPricingApplied, setCustomerPricingApplied] = useState(false);
  const [applyingCustomerPricing, setApplyingCustomerPricing] = useState(false);
  const customerPricingAppliedItemIdsRef = useRef<string>('');

  // AI per-line categorization suggestions (full allocation chain)
  const [aiPerLineCategorization, setAiPerLineCategorization] = useState<Array<{
    index: number;
    account_code: string;
    account_name: string;
    confidence: number;
    reasons: string[];
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
    suggested_vehicle_id?: string | null;
    suggested_vehicle_name?: string | null;
  }>>([]);
  // Whole-document GL suggestion — used by the single-GL allocation bar.
  const [aiDocSuggestion, setAiDocSuggestion] = useState<{
    index: number;
    account_code: string;
    account_name: string;
    confidence: number;
    reasons: string[];
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
    suggested_vehicle_id?: string | null;
    suggested_vehicle_name?: string | null;
  } | null>(null);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const aiFetchAbortRef = useRef<AbortController | null>(null);

  // Track imported work items for billing linkage
  const [importedWorkItemIds, setImportedWorkItemIds] = useState<string[]>([]);
  const [importedTimeEntryIds, setImportedTimeEntryIds] = useState<string[]>([]);

  // Line item comment thread state
  const [lineItemCommentThread, setLineItemCommentThread] = useState<{
    isOpen: boolean;
    lineItemIndex: number | null;
  }>({ isOpen: false, lineItemIndex: null });

  // --- Undo stack for line item edits ---
  // Stores snapshots of items before each mutation so the user can undo
  // accidental deletions, drags, or field changes with Ctrl+Z.
  const undoStackRef = useRef<DocumentLineItem[][]>([]);
  const [canUndo, setCanUndo] = useState(false);

  // Wrap setItems to push the previous state onto the undo stack
  const setItemsWithUndo = useCallback((
    updater: DocumentLineItem[] | ((prev: DocumentLineItem[]) => DocumentLineItem[]),
  ) => {
    setItems(prev => {
      // Snapshot the previous state for undo
      undoStackRef.current.push(prev.map(item => ({ ...item })));
      if (undoStackRef.current.length > 50) {
        undoStackRef.current.shift(); // cap stack depth
      }
      setCanUndo(undoStackRef.current.length > 0);
      return typeof updater === 'function' ? updater(prev) : updater;
    });
  }, [setItems]);

  const undoLastItemChange = useCallback(() => {
    const prev = undoStackRef.current.pop();
    if (prev) {
      setItems(prev);
      setCanUndo(undoStackRef.current.length > 0);
      toast.success('Undone');
    } else {
      toast.error('Nothing to undo');
    }
  }, [setItems]);

  const clearUndoStack = useCallback(() => {
    undoStackRef.current = [];
    setCanUndo(false);
  }, []);

  const preferredDefaultTaxRate = useMemo(
    () => {
      if (settingsDefaultTaxRateId) {
        const fromSettings = taxRates.find(r => r.id === settingsDefaultTaxRateId);
        if (fromSettings) return fromSettings;
      }
      if (selectedContact?.tax_rate_id) {
        const contactTaxRate = taxRates.find(r => r.id === selectedContact.tax_rate_id);
        if (contactTaxRate) return contactTaxRate;
      }
      return getPreferredDefaultTaxRate(taxRates);
    },
    [taxRates, settingsDefaultTaxRateId, selectedContact],
  );

  // ============================================================================
  // ITEM CRUD
  // ============================================================================

  const handleItemChange = useCallback((index: number, field: keyof DocumentLineItem, value: DocumentLineItemValue) => {
    setItemsWithUndo(prevItems => {
      const newItems = [...prevItems];
      newItems[index] = { ...newItems[index], [field]: value };

      const item = newItems[index];
      const lineSubtotal = multiplyMoney(item.quantity || 0, item.unit_price || 0);
      const discountAmount = roundMoney(item.discount_amount || 0);
      const lineAfterDiscount = roundMoney(lineSubtotal - discountAmount);
      if (item.tax_inclusive) {
        // Tax-inclusive: unit_price already includes tax. Extract tax portion.
        const taxRate = (item.tax_rate || 0) / 100;
        const taxDivisor = 1 + taxRate;
        item.tax_amount = roundMoney(lineAfterDiscount - (lineAfterDiscount / taxDivisor));
        // Line total is ex-VAT; tax is added at the document summary.
        item.line_total = roundMoney(lineAfterDiscount - item.tax_amount);
      } else {
        item.tax_amount = roundMoney((lineAfterDiscount * (item.tax_rate || 0)) / 100);
        // Line total is ex-VAT; tax is added at the document summary.
        item.line_total = roundMoney(lineAfterDiscount);
      }

      return newItems;
    });
  }, [setItemsWithUndo]);

  const handleRemoveItem = useCallback((index: number) => {
    setItemsWithUndo(prevItems => prevItems.filter((_, i) => i !== index));
    setSelectedItemIndices(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  }, [setItemsWithUndo]);

  const handleLineItemAccountChange = useCallback((index: number, accountCode: string) => {
    setItemsWithUndo(prevItems => {
      const newItems = [...prevItems];
      const item = { ...newItems[index] };

      const newAllocationType = getDerivedAllocationType(expenseAccounts, accountCode);

      const needsStockLink = newAllocationType === 'stock';
      const needsEquipmentLink = newAllocationType === 'equipment';

      item.expense_account_code = accountCode;
      item.allocation_type = newAllocationType;
      // Job/customer links are never cleared automatically — a link captured
      // in ChildEntryForm stays as dormant provenance on non-'job' lines and
      // reactivates if the admin re-selects Job allocation. Unlinking only
      // happens when the user explicitly clears the job selector.
      if (!needsStockLink) {
        (item as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = '';
        (item as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = '';
        (item as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = '';
      }
      if (!needsEquipmentLink) {
        item.linked_equipment_item_id = '';
        (item as DocumentLineItem & { linked_equipment_name?: string }).linked_equipment_name = '';
      }

      newItems[index] = item;
      return newItems;
    });
  }, [expenseAccounts, setItemsWithUndo]);

  const handleLineItemAllocationTypeChange = useCallback((index: number, allocType: AllocationType) => {
    setItemsWithUndo(prevItems => {
      const newItems = [...prevItems];
      const item = { ...newItems[index] };

      const needsStockLink = allocType === 'stock';
      const needsEquipmentLink = allocType === 'equipment';

      item.allocation_type = allocType;
      // Job/customer links are never cleared automatically — a link stays as
      // dormant provenance on non-'job' allocations and reactivates if the
      // user re-selects Job. Explicit unlink only via the job selector.
      if (!needsStockLink) {
        (item as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = '';
        (item as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = '';
        (item as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = '';
      }
      if (!needsEquipmentLink) {
        item.linked_equipment_item_id = '';
        (item as DocumentLineItem & { linked_equipment_name?: string }).linked_equipment_name = '';
      }

      newItems[index] = item;
      return newItems;
    });
  }, [setItemsWithUndo]);

  const handleSplitLineItem = useCallback((idx: number, qtyToSplit: number) => {
    setItemsWithUndo(prevItems => {
      const item = prevItems[idx];
      if (!item || qtyToSplit <= 0 || qtyToSplit >= (item.quantity || 0)) return prevItems;
      const remainingQty = (item.quantity || 0) - qtyToSplit;
      const splitLineSubtotal = multiplyMoney(qtyToSplit, item.unit_price || 0);
      const splitDiscountAmount = roundMoney((item.discount_amount || 0) * (qtyToSplit / (item.quantity || 1)));
      const splitLineAfterDiscount = roundMoney(splitLineSubtotal - splitDiscountAmount);
      let splitTaxAmount: number;
      let splitLineTotal: number;
      if (item.tax_inclusive) {
        const taxRate = (item.tax_rate || 0) / 100;
        const taxDivisor = 1 + taxRate;
        splitTaxAmount = roundMoney(splitLineAfterDiscount - (splitLineAfterDiscount / taxDivisor));
        // Line total is ex-VAT.
        splitLineTotal = roundMoney(splitLineAfterDiscount - splitTaxAmount);
      } else {
        splitTaxAmount = roundMoney((splitLineAfterDiscount * (item.tax_rate || 0)) / 100);
        // Line total is ex-VAT.
        splitLineTotal = roundMoney(splitLineAfterDiscount);
      }
      const splitItem: DocumentLineItem = {
        ...item,
        id: undefined,
        quantity: qtyToSplit,
        discount_amount: splitDiscountAmount,
        tax_amount: splitTaxAmount,
        line_total: splitLineTotal,
        sort_order: (item.sort_order || 0) + 1,
        split_parent_ref: item.id != null ? String(item.id) : (item.split_parent_ref ?? undefined),
      };
      const remainingLineSubtotal = multiplyMoney(remainingQty, item.unit_price || 0);
      const remainingDiscountAmount = roundMoney((item.discount_amount || 0) - splitDiscountAmount);
      const remainingLineAfterDiscount = roundMoney(remainingLineSubtotal - remainingDiscountAmount);
      let remainingTaxAmount: number;
      let remainingLineTotal: number;
      if (item.tax_inclusive) {
        const taxRate = (item.tax_rate || 0) / 100;
        const taxDivisor = 1 + taxRate;
        remainingTaxAmount = roundMoney(remainingLineAfterDiscount - (remainingLineAfterDiscount / taxDivisor));
        // Line total is ex-VAT.
        remainingLineTotal = roundMoney(remainingLineAfterDiscount - remainingTaxAmount);
      } else {
        remainingTaxAmount = roundMoney((remainingLineAfterDiscount * (item.tax_rate || 0)) / 100);
        // Line total is ex-VAT.
        remainingLineTotal = roundMoney(remainingLineAfterDiscount);
      }
      return [
        ...prevItems.slice(0, idx),
        { ...item, quantity: remainingQty, discount_amount: remainingDiscountAmount, tax_amount: remainingTaxAmount, line_total: remainingLineTotal },
        splitItem,
        ...prevItems.slice(idx + 1),
      ];
    });
    setSplitIdx(null);
    setSplitQty(1);
  }, [setItemsWithUndo]);

  // ============================================================================
  // ALLOCATION DIMENSION & SPLIT HELPERS
  // ============================================================================

  const getLineItemDimensions = useCallback((idx: number): LineItemAllocation[] => {
    return items[idx]?.allocations?.dimensions ?? [];
  }, [items]);

  const getLineItemSplits = useCallback((idx: number): SplitAllocation[] => {
    return items[idx]?.allocations?.splits ?? [];
  }, [items]);

  const updateLineItemAllocations = useCallback((idx: number, allocations: { dimensions?: LineItemAllocation[]; splits?: SplitAllocation[] }) => {
    setItemsWithUndo(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const current = item.allocations ?? {};
      const next = { ...current };
      if (allocations.dimensions !== undefined) next.dimensions = allocations.dimensions;
      if (allocations.splits !== undefined) next.splits = allocations.splits;
      const hasDimensions = next.dimensions && next.dimensions.length > 0;
      const hasSplits = next.splits && next.splits.length > 0;
      return { ...item, allocations: (hasDimensions || hasSplits) ? next : null };
    }));
  }, [setItemsWithUndo]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItemsWithUndo(prevItems => {
        const oldIndex = Number(active.id);
        const newIndex = Number(over.id);
        return arrayMove(prevItems, oldIndex, newIndex).map((item, idx) => ({
          ...item,
          sort_order: idx + 1
        }));
      });
    }
  }, [setItemsWithUndo]);

  // ============================================================================
  // ITEM INSERT / SELECT
  // ============================================================================

  const mapPurchaseSourcingToLineItem = useCallback((purchaseSourcing?: PurchaseLineSourcingDetails): Partial<DocumentLineItem> => {
    if (!purchaseSourcing) {
      return {};
    }

    return {
      purchase_supplier_mapping_id: purchaseSourcing.supplier_mapping_id,
      purchase_supplier_contact_id: purchaseSourcing.supplier_contact_id,
      purchase_supplier_name: purchaseSourcing.supplier_name ?? undefined,
      purchase_supplier_sku: purchaseSourcing.supplier_sku ?? undefined,
      purchase_supplier_currency: purchaseSourcing.currency_code ?? undefined,
      purchase_supplier_cost_price: purchaseSourcing.cost_price ?? undefined,
      purchase_supplier_is_preferred: purchaseSourcing.is_preferred,
      purchase_supplier_priority_rank: purchaseSourcing.priority_rank ?? undefined,
      purchase_supplier_lead_time_days: purchaseSourcing.lead_time_days ?? undefined,
      purchase_supplier_selection_reason: purchaseSourcing.selection_reason ?? undefined,
      purchase_supplier_fallback_reason: purchaseSourcing.fallback_reason ?? undefined,
      purchase_supplier_price_source: purchaseSourcing.price_source,
      purchase_supplier_matches_document_contact: purchaseSourcing.matches_document_contact,
    };
  }, []);

  const handleItemSelected = useCallback((item: FinanceItem, options: ItemSelectionOptions) => {
    const effectivePrice = options.price_override || item.unit_price;
    const quantity = options.quantity || 1;
    const discountPercentage = options.discount_percentage || 0;

    const defaultTaxRate = preferredDefaultTaxRate;
    const hasItemTaxRate = item.tax_rate_id && item.tax_rate_percentage !== undefined;
    const taxRateId = hasItemTaxRate ? item.tax_rate_id : defaultTaxRate?.id;
    const taxRatePercentage = hasItemTaxRate
      ? parseTaxRateValue(item.tax_rate_percentage)
      : parseTaxRateValue(defaultTaxRate?.rate);

    const lineSubtotal = multiplyMoney(quantity, effectivePrice);
    const discountAmount = roundMoney(lineSubtotal * (discountPercentage / 100));
    const lineAfterDiscount = roundMoney(lineSubtotal - discountAmount);
    let taxAmount: number;
    let lineTotal: number;
    if (item.is_tax_inclusive && taxRatePercentage > 0) {
      const taxDivisor = 1 + taxRatePercentage / 100;
      taxAmount = roundMoney(lineAfterDiscount - (lineAfterDiscount / taxDivisor));
      // Line total is ex-VAT; tax is added at the document summary.
      lineTotal = roundMoney(lineAfterDiscount - taxAmount);
    } else {
      taxAmount = roundMoney((lineAfterDiscount * taxRatePercentage) / 100);
      // Line total is ex-VAT; tax is added at the document summary.
      lineTotal = roundMoney(lineAfterDiscount);
    }

    const newItem: DocumentLineItem = {
      item_id: item.id,
      item_code: item.item_code,
      description: item.name,
      quantity,
      unit: item.unit_of_measure,
      unit_price: effectivePrice,
      discount_percentage: discountPercentage,
      discount_amount: discountAmount,
      tax_rate: taxRatePercentage,
      tax_rate_id: taxRateId ?? undefined,
      tax_amount: taxAmount,
      line_total: lineTotal,
      track_inventory: item.track_inventory,
      tax_inclusive: item.is_tax_inclusive,
      sort_order: 0,
      category_id: item.category_id,
      category_name: item.category_name,
      ...mapPurchaseSourcingToLineItem(options.purchase_sourcing),
    };

    setItemsWithUndo((prevItems) => ([
      ...prevItems,
      {
        ...newItem,
        sort_order: prevItems.length,
      },
    ]));
  }, [preferredDefaultTaxRate, mapPurchaseSourcingToLineItem, setItemsWithUndo]);

  const handleInlineItemSelect = useCallback((item: FinanceItem, indexOverride?: number) => {
    const targetIndex = indexOverride ?? inlineSearchIndex;
    setInlineSearchIndex(null);

    const defaultTaxRate = preferredDefaultTaxRate;
    const hasItemTaxRate = item.tax_rate_id && item.tax_rate_percentage !== undefined;
    const taxRateId = hasItemTaxRate ? item.tax_rate_id : defaultTaxRate?.id;
    const taxRatePercentage = hasItemTaxRate
      ? parseTaxRateValue(item.tax_rate_percentage)
      : parseTaxRateValue(defaultTaxRate?.rate);

    const quantity = 1;
    const lineSubtotal = multiplyMoney(quantity, item.unit_price);
    let taxAmount: number;
    let lineTotal: number;
    if (item.is_tax_inclusive && taxRatePercentage > 0) {
      const taxDivisor = 1 + taxRatePercentage / 100;
      taxAmount = roundMoney(lineSubtotal - (lineSubtotal / taxDivisor));
      // Line total is ex-VAT; tax is added at the document summary.
      lineTotal = roundMoney(lineSubtotal - taxAmount);
    } else {
      taxAmount = roundMoney((lineSubtotal * taxRatePercentage) / 100);
      // Line total is ex-VAT; tax is added at the document summary.
      lineTotal = roundMoney(lineSubtotal);
    }

    setItemsWithUndo(prevItems => prevItems.map((line, idx) => {
      if (idx !== targetIndex) return line;
      return {
        ...line,
        item_id: item.id,
        item_code: item.item_code,
        description: item.name,
        quantity,
        unit: item.unit_of_measure,
        unit_price: item.unit_price,
        discount_percentage: 0,
        discount_amount: 0,
        tax_rate: taxRatePercentage,
        tax_rate_id: taxRateId ?? undefined,
        tax_amount: taxAmount,
        line_total: lineTotal,
        category_id: item.category_id,
        category_name: item.category_name,
        track_inventory: item.track_inventory,
        tax_inclusive: item.is_tax_inclusive,
        ...mapPurchaseSourcingToLineItem(item.purchase_sourcing),
      };
    }));
  }, [inlineSearchIndex, preferredDefaultTaxRate, setItemsWithUndo, mapPurchaseSourcingToLineItem]);

  const handleAddStructuredLine = useCallback((lineType: 'header' | 'comment' | 'spacer' | 'subtotal') => {
    setItemsWithUndo(prevItems => {
      if (lineType === 'subtotal') {
        const subtotal = roundMoney(
          prevItems
            .filter(item => item.line_type === 'item' || !item.line_type)
            .reduce((sum, item) => sum + (Number(item.line_total) || 0), 0),
        );

        return [
          ...prevItems,
          {
            description: 'Subtotal',
            quantity: 0,
            unit_price: 0,
            tax_rate: 0,
            tax_amount: 0,
            line_total: subtotal,
            line_type: 'subtotal',
            sort_order: prevItems.length,
          },
        ];
      }

      return [
        ...prevItems,
        {
          description: '',
          quantity: 0,
          unit_price: 0,
          tax_rate: 0,
          tax_amount: 0,
          line_total: 0,
          line_type: lineType,
          tax_rate_id: undefined,
          sort_order: prevItems.length,
        },
      ];
    });
  }, [setItemsWithUndo]);

  const toggleInsertMenu = useCallback((placement: InsertMenuPlacement) => {
    setOpenInsertMenu(prev => {
      if (prev === placement) {
        setHighlightedInsertIndex(-1);
        return null;
      }
      setHighlightedInsertIndex(0);
      return placement;
    });
  }, []);

  const handleInsertMenuAction = useCallback((action: 'quick_row' | 'item' | 'header' | 'comment' | 'spacer' | 'subtotal') => {
    setOpenInsertMenu(null);
    setHighlightedInsertIndex(-1);

    if (action === 'item') {
      setShowItemSearch(true);
      return;
    }

    if (action === 'quick_row') {
      const newIndex = items.length;
      const defaultTaxRate = preferredDefaultTaxRate;
      const taxRate = parseTaxRateValue(defaultTaxRate?.rate) || 0;
      setItemsWithUndo(prevItems => {
        const newItem: DocumentLineItem = {
          description: '',
          quantity: 1,
          unit: 'each',
          unit_price: 0,
          tax_rate: taxRate,
          tax_amount: 0,
          line_total: 0,
          line_type: 'item',
          tax_rate_id: defaultTaxRate?.id ?? undefined,
          sort_order: prevItems.length,
        };
        return [...prevItems, newItem];
      });
      setInlineSearchIndex(newIndex);
      return;
    }

    handleAddStructuredLine(action);
  }, [handleAddStructuredLine, items.length, preferredDefaultTaxRate, setItemsWithUndo]);

  // ============================================================================
  // AI CATEGORIZATION
  // ============================================================================

  const fetchAISuggestions = useCallback(async (forceRefresh = false) => {
    const workItemId = doc?.id || formData.id;
    if (!workItemId) return;

    const itemLines = items.filter(it => {
      const lt = it.line_type || 'item';
      return lt === 'item' || lt === 'expense_line';
    });
    if (itemLines.length === 0) return;

    // GL-suggestion fetch is categorization-only:
    //  - Supplier matching belongs to intake (before the modal opens), so the
    //    modal never asks the API to match a supplier — that also prevents the
    //    route from auto-writing contact_id / matched_contact_id.
    //  - per-line categorization only runs in per-line GL mode (one AI call per
    //    line, ~10s each). In single-GL mode the whole-document categorization
    //    is enough — one AI call.
    const wantPerLine = glAllocationMode === 'per_line';

    // Abort any in-flight suggestion request — a newer call supersedes it
    // (e.g. re-analyze while the auto-fetch is still running).
    aiFetchAbortRef.current?.abort();
    const abortController = new AbortController();
    aiFetchAbortRef.current = abortController;

    setAiSuggestionsLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const vendorName = selectedContact?.company_name || selectedContact?.display_name || formData.contact_name || null;

      // Extract suggested category from the document's OCR/extraction metadata
      const docCustomFields = (doc?.custom_fields ?? {}) as Record<string, unknown>;
      const extractionData = (docCustomFields.extraction ?? docCustomFields.receipt_ai_validation ?? {}) as Record<string, unknown>;
      const suggestedCategory = (extractionData.suggested_category as string) || (docCustomFields.category as string) || null;

      // Extract vehicle linkage and fuel context from the document's custom_fields
      // so the AI can proactively suggest vehicle-related GL accounts.
      // The actual entry_type (e.g. 'fuel', 'toll') lives in custom_fields, not doc_type.
      const actualEntryType = (docCustomFields.entry_type as string) || null;
      const vehicleId = (docCustomFields.vehicle_id as string) || null;
      const vehicleReg = (docCustomFields.vehicle_registration as string) || null;
      const odometer = (docCustomFields.odometer as number | string | undefined) ?? null;
      const litres = (docCustomFields.litres as number | string | undefined) ?? null;
      const fuelType = (docCustomFields.fuel_type as string) || null;

      const response = await fetch('/api/v1/finance/ai-suggestions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
        body: JSON.stringify({
          vendor_name: vendorName,
          suggested_category: suggestedCategory,
          line_items: items.map(li => ({
            description: li.description || '',
            quantity: li.quantity || 1,
            unit_price: li.unit_price || 0,
            total: li.line_total || li.amount || 0,
          })),
          total_amount: formData.grand_total || null,
          entry_type: actualEntryType || doc_type || null,
          vehicle_id: vehicleId,
          vehicle_registration: vehicleReg,
          odometer,
          litres,
          fuel_type: fuelType,
          per_line_categorization: wantPerLine,
          request_supplier_match: false,
          work_item_id: String(workItemId),
          force_refresh: forceRefresh,
        }),
      });

      if (!response.ok) return;
      const data = await response.json();

      // Merge with semantic intake historical allocations for additional
      // customer link suggestions (supplements the API's built-in historical query).
      let semanticAllocations: Array<{
        expense_account_code: string | null;
        matched_contact_id: number | null;
        matched_supplier_name: string | null;
        similarity: number;
      }> = [];
      if (vendorName && isExpenseLikeDoc) {
        try {
          const ctx = await semanticIntake.batchExpenseContext({
            queryText: vendorName,
            excludeWorkItemId: doc?.id ? Number(doc.id) : undefined,
            workspace: doc_type,
            limit: 5,
            persistToWorkItemId: doc?.id,
          });
          semanticAllocations = ctx?.approvedGLAllocations ?? [];
        } catch {
          // Non-critical — continue without semantic data
        }
      }

      const pickSemanticCustomer = (accountCode: string) =>
        semanticAllocations.find(
          (a) => a.expense_account_code === accountCode && a.matched_contact_id,
        );

      // Whole-document suggestion — drives the single-GL allocation bar.
      const docCat = (data.categorization && typeof data.categorization === 'object')
        ? data.categorization as Record<string, unknown>
        : null;
      const docAccountCode = String(docCat?.account_code || '');
      if (docAccountCode) {
        const semanticMatch = pickSemanticCustomer(docAccountCode);
        const hasVehicleCtx = Boolean(vehicleId || vehicleReg);
        setAiDocSuggestion({
          index: 0,
          account_code: docAccountCode,
          account_name: String(docCat?.account_name || ''),
          confidence: Number(docCat?.confidence) || 0,
          reasons: Array.isArray(docCat?.reasons) ? docCat.reasons as string[] : [],
          // Mirror the route's per-line derivation: default 'overhead' unless
          // the document is vehicle-linked and the account allows it.
          allocation_type: hasVehicleCtx
            ? getPreferredAllocationType(expenseAccounts, docAccountCode, true)
            : 'overhead',
          inventory_item_match: (docCat?.inventory_item_match as InventoryItemMatch | null | undefined) ?? null,
          suggested_customer_id: semanticMatch?.matched_contact_id ? String(semanticMatch.matched_contact_id) : null,
          suggested_customer_name: semanticMatch?.matched_supplier_name ?? null,
          suggested_vehicle_id: hasVehicleCtx ? vehicleId : null,
          suggested_vehicle_name: hasVehicleCtx ? vehicleReg : null,
        });
      } else {
        setAiDocSuggestion(null);
      }

      if (data.perLineCategorization && Array.isArray(data.perLineCategorization)) {
        const merged = (data.perLineCategorization as Array<Record<string, unknown>>).map((entry) => {
          const accountCode = String(entry.account_code || '');
          // If the API didn't provide a customer suggestion, try semantic intake
          if (accountCode && !entry.suggested_customer_id && semanticAllocations.length > 0) {
            const semanticMatch = pickSemanticCustomer(accountCode);
            if (semanticMatch) {
              entry.suggested_customer_id = String(semanticMatch.matched_contact_id);
              entry.suggested_customer_name = semanticMatch.matched_supplier_name ?? null;
            }
          }
          return entry as {
            index: number;
            account_code: string;
            account_name: string;
            confidence: number;
            reasons: string[];
            allocation_type?: AllocationType;
            inventory_item_match?: InventoryItemMatch | null;
            suggested_customer_id?: string | null;
            suggested_customer_name?: string | null;
            suggested_job_id?: string | null;
            suggested_job_name?: string | null;
            suggested_vehicle_id?: string | null;
            suggested_vehicle_name?: string | null;
          };
        });
        setAiPerLineCategorization(merged);
      } else if (wantPerLine) {
        setAiPerLineCategorization([]);
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      console.error('[DocumentModal] AI suggestions failed:', err);
    } finally {
      if (aiFetchAbortRef.current === abortController) {
        aiFetchAbortRef.current = null;
        setAiSuggestionsLoading(false);
      }
    }
  }, [doc?.id, doc?.custom_fields, formData.id, formData.grand_total, formData.contact_name, selectedContact, items, doc_type, semanticIntake, isExpenseLikeDoc, glAllocationMode, expenseAccounts]);

  const handleApplyAICategorization = useCallback((accountCode: string, lineIndex: number) => {
    // Use the AI suggestion's allocation_type when available — this respects
    // the AI route's decision to default to 'overhead' when there's no job
    // context, instead of always deriving 'job' for COGS accounts.
    const aiSuggestion = aiPerLineCategorization.find(s => s.index === lineIndex);
    const rawType = aiSuggestion?.allocation_type || getDerivedAllocationType(expenseAccounts, accountCode);
    // 'overhead' is the generic default — upgrade to 'vehicle' when the
    // document is vehicle-linked and the account permits it, so accepting a
    // suggestion like "Travel & Accommodation" keeps the captured vehicle.
    const derivedType = (rawType === 'overhead' && docHasVehicleLink)
      ? getPreferredAllocationType(expenseAccounts, accountCode, true)
      : rawType;
    const needsStockLink = derivedType === 'stock';
    const needsEquipmentLink = derivedType === 'equipment';
    const needsVehicleLink = derivedType === 'vehicle';
    setItemsWithUndo(prevItems => prevItems.map((item, i) => {
      if (i !== lineIndex) return item;
      const updated: DocumentLineItem = {
        ...item,
        expense_account_code: accountCode,
        allocation_type: derivedType,
      };
      // Job/customer links are never cleared automatically — they stay as
      // dormant provenance on non-'job' lines.
      if (!needsStockLink) {
        (updated as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = '';
        (updated as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = '';
        (updated as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = '';
      }
      if (!needsEquipmentLink) {
        updated.linked_equipment_item_id = '';
        (updated as DocumentLineItem & { linked_equipment_name?: string }).linked_equipment_name = '';
      }
      if (!needsVehicleLink) {
        (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id = '';
        (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name = '';
      } else {
        const docCf = (doc?.custom_fields ?? {}) as Record<string, unknown>;
        (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id =
          (aiSuggestion as { suggested_vehicle_id?: string | null } | undefined)?.suggested_vehicle_id
          || (docCf.vehicle_id as string | undefined) || (doc?.vehicle_id as string | undefined) || '';
        (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name =
          (aiSuggestion as { suggested_vehicle_name?: string | null } | undefined)?.suggested_vehicle_name
          || (docCf.vehicle_registration as string | undefined) || (doc?.vehicle_registration as string | undefined) || '';
      }
      return updated;
    }));
  }, [expenseAccounts, setItemsWithUndo, aiPerLineCategorization, docHasVehicleLink, doc]);

  /**
   * Apply a full allocation chain suggestion (GL + allocation type + customer/job
   * or inventory/equipment) to a line item in a single operation.
   * This powers the "Apply All" one-click acceptance from the AI suggestion panel.
   */
  const handleApplyFullAllocation = useCallback((lineIndex: number, suggestion: {
    account_code: string;
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
    suggested_vehicle_id?: string | null;
    suggested_vehicle_name?: string | null;
  }) => {
    if (!suggestion.account_code) return;
    setItemsWithUndo(prevItems => prevItems.map((item, i) => {
      if (i !== lineIndex) return item;
      const accountCode = suggestion.account_code;
      const rawType = suggestion.allocation_type || getDerivedAllocationType(expenseAccounts, accountCode);
      const derivedType = (rawType === 'overhead' && docHasVehicleLink)
        ? getPreferredAllocationType(expenseAccounts, accountCode, true)
        : rawType;
      const needsJobLink = derivedType === 'job';
      const needsStockLink = derivedType === 'stock';
      const needsEquipmentLink = derivedType === 'equipment';
      const needsVehicleLink = derivedType === 'vehicle';

      const updated: DocumentLineItem = {
        ...item,
        expense_account_code: accountCode,
        allocation_type: derivedType,
      };

      // Job/customer links are never cleared automatically — a link stays as
      // dormant provenance on non-'job' lines. When the suggestion targets
      // job allocation, apply its values over any existing links.
      if (needsJobLink) {
        if (suggestion.suggested_customer_id) {
          (updated as DocumentLineItem & { linked_customer_id?: string }).linked_customer_id = suggestion.suggested_customer_id;
          (updated as DocumentLineItem & { linked_customer_name?: string }).linked_customer_name = suggestion.suggested_customer_name || '';
        }
        if (suggestion.suggested_job_id) {
          updated.linked_job_id = suggestion.suggested_job_id;
          (updated as DocumentLineItem & { linked_job_name?: string }).linked_job_name = suggestion.suggested_job_name || '';
        }
      }

      if (!needsStockLink) {
        (updated as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = '';
        (updated as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = '';
        (updated as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = '';
      } else if (suggestion.inventory_item_match) {
        (updated as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = suggestion.inventory_item_match.item_id;
        (updated as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = suggestion.inventory_item_match.item_title;
        (updated as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = suggestion.inventory_item_match.sku || '';
      }

      if (!needsEquipmentLink) {
        updated.linked_equipment_item_id = '';
        (updated as DocumentLineItem & { linked_equipment_name?: string }).linked_equipment_name = '';
      }

      if (!needsVehicleLink) {
        (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id = '';
        (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name = '';
      } else {
        const docCf = (doc?.custom_fields ?? {}) as Record<string, unknown>;
        (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id =
          suggestion.suggested_vehicle_id
          || (docCf.vehicle_id as string | undefined) || (doc?.vehicle_id as string | undefined) || '';
        (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name =
          suggestion.suggested_vehicle_name
          || (docCf.vehicle_registration as string | undefined) || (doc?.vehicle_registration as string | undefined) || '';
      }

      return updated;
    }));
  }, [expenseAccounts, setItemsWithUndo, docHasVehicleLink, doc]);

  /**
   * Apply a single GL allocation chain to ALL line items at once.
   * Used in "single GL" mode where one allocation applies to the entire document.
   * Clears per-line overrides for all item/expense_line/tip rows.
   */
  const handleApplySingleGLAllocation = useCallback((suggestion: {
    account_code: string;
    allocation_type?: AllocationType;
    inventory_item_match?: InventoryItemMatch | null;
    suggested_customer_id?: string | null;
    suggested_customer_name?: string | null;
    suggested_job_id?: string | null;
    suggested_job_name?: string | null;
    suggested_vehicle_id?: string | null;
    suggested_vehicle_name?: string | null;
  }) => {
    if (!suggestion.account_code) return;
    const accountCode = suggestion.account_code;
    // 'overhead' (or absent) is the generic default — upgrade to 'vehicle'
    // when the document is vehicle-linked and the account permits it, so a
    // suggestion like "Travel & Accommodation" keeps the captured vehicle
    // instead of silently unlinking it. Explicit job/stock/vehicle/suspense
    // types pass through unchanged.
    const derivedType = (!suggestion.allocation_type || suggestion.allocation_type === 'overhead')
      ? getPreferredAllocationType(expenseAccounts, accountCode, docHasVehicleLink)
      : suggestion.allocation_type;
    const needsJobLink = derivedType === 'job';
    const needsStockLink = derivedType === 'stock';
    const needsVehicleLink = derivedType === 'vehicle';

    setItemsWithUndo(prevItems => prevItems.map((item) => {
      const lt = item.line_type || 'item';
      // Only apply to productive lines (skip header/comment/spacer/subtotal)
      if (lt === 'header' || lt === 'comment' || lt === 'spacer' || lt === 'subtotal') return item;

      const updated: DocumentLineItem = {
        ...item,
        expense_account_code: accountCode,
        allocation_type: derivedType,
      };

      // Job/customer links are never cleared automatically — a technician's
      // captured link stays as dormant provenance on non-'job' lines. When
      // the suggestion targets job allocation, apply its values over any
      // existing links.
      if (needsJobLink) {
        if (suggestion.suggested_customer_id) {
          (updated as DocumentLineItem & { linked_customer_id?: string }).linked_customer_id = suggestion.suggested_customer_id;
          (updated as DocumentLineItem & { linked_customer_name?: string }).linked_customer_name = suggestion.suggested_customer_name || '';
        }
        // else: keep existing linked_customer_id/name from the item
        if (suggestion.suggested_job_id) {
          updated.linked_job_id = suggestion.suggested_job_id;
          (updated as DocumentLineItem & { linked_job_name?: string }).linked_job_name = suggestion.suggested_job_name || '';
        }
        // else: keep existing linked_job_id/name from the item
      }

      if (!needsStockLink) {
        (updated as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = '';
        (updated as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = '';
        (updated as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = '';
      } else if (suggestion.inventory_item_match) {
        (updated as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = suggestion.inventory_item_match.item_id;
        (updated as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = suggestion.inventory_item_match.item_title;
        (updated as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = suggestion.inventory_item_match.sku || '';
      }

      // Clear equipment link if not equipment allocation
      if (derivedType !== 'equipment') {
        updated.linked_equipment_item_id = '';
        (updated as DocumentLineItem & { linked_equipment_name?: string }).linked_equipment_name = '';
      }

      // Clear or set vehicle link based on allocation type
      if (!needsVehicleLink) {
        (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id = '';
        (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name = '';
      } else {
        // For vehicle allocation: only replace links if the suggestion provides
        // new values; otherwise preserve the existing ones, falling back to the
        // document's captured vehicle so the link is restored on apply.
        const docCf = (doc?.custom_fields ?? {}) as Record<string, unknown>;
        const vehicleId = suggestion.suggested_vehicle_id
          || (docCf.vehicle_id as string | undefined) || (doc?.vehicle_id as string | undefined);
        if (vehicleId) {
          (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id = vehicleId;
          (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name =
            suggestion.suggested_vehicle_name
            || (docCf.vehicle_registration as string | undefined) || (doc?.vehicle_registration as string | undefined) || '';
        }
      }

      return updated;
    }));
  }, [expenseAccounts, setItemsWithUndo, docHasVehicleLink, doc]);

  // Sync allocation_type from GL account when modal opens or items are initialized.
  // This ensures the correct dropdown renders without requiring user interaction.
  // However, if the item already has allocation_type='job' with a linked_job_id
  // (from SmartTask capture), preserve it — the technician's original allocation
  // should not be overwritten by GL-derived defaults. The admin can override
  // explicitly by changing the allocation type dropdown.
  useEffect(() => {
    if (!isOpen) return;
    if (doc_type !== 'expense' && doc_type !== 'supplier_invoice' && doc_type !== 'din') return;
    if (items.length === 0) return;
    if (expenseAccounts.length === 0) return;

    setItemsWithUndo(prevItems => {
      let changed = false;
      const newItems = prevItems.map((item) => {
        const accountCode = (item as DocumentLineItem & { expense_account_code?: string }).expense_account_code;
        if (!accountCode) return item;
        const derivedType = getPreferredAllocationType(expenseAccounts, accountCode, docHasVehicleLink);
        const currentType = (item.allocation_type || 'overhead') as AllocationType;
        if (currentType === derivedType) return item;
        // Preserve technician's job allocation: if the item already has
        // allocation_type='job' and a linked_job_id, don't override it with
        // the GL-derived type. The GL account may default to 'overhead' but
        // the technician explicitly linked this expense to a job.
        if (currentType === 'job' && item.linked_job_id) return item;
        // Same for a captured vehicle link — don't downgrade 'vehicle' to
        // 'overhead' just because the GL's subtype default is overhead.
        if (currentType === 'vehicle' && (item as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id) return item;
        changed = true;
        const needsStockLink = derivedType === 'stock';
        const needsEquipmentLink = derivedType === 'equipment';
        const needsVehicleLink = derivedType === 'vehicle';
        const updated: DocumentLineItem = { ...item, allocation_type: derivedType };
        // Job/customer links are never cleared automatically — dormant
        // provenance only; they reactivate on Job allocation.
        if (!needsStockLink) {
          (updated as DocumentLineItem & { linked_inventory_item_id?: string }).linked_inventory_item_id = '';
          (updated as DocumentLineItem & { linked_inventory_name?: string }).linked_inventory_name = '';
          (updated as DocumentLineItem & { linked_inventory_sku?: string }).linked_inventory_sku = '';
        }
        if (!needsEquipmentLink) {
          updated.linked_equipment_item_id = '';
          (updated as DocumentLineItem & { linked_equipment_name?: string }).linked_equipment_name = '';
        }
        if (!needsVehicleLink) {
          (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id = '';
          (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name = '';
        } else if (!(item as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id) {
          const docCf = (doc?.custom_fields ?? {}) as Record<string, unknown>;
          const docVehicleId = (docCf.vehicle_id as string | undefined) || (doc?.vehicle_id as string | undefined);
          if (docVehicleId) {
            (updated as DocumentLineItem & { linked_vehicle_id?: string }).linked_vehicle_id = docVehicleId;
            (updated as DocumentLineItem & { linked_vehicle_name?: string }).linked_vehicle_name =
              (docCf.vehicle_registration as string | undefined) || (doc?.vehicle_registration as string | undefined) || '';
          }
        }
        return updated;
      });
      return changed ? newItems : prevItems;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only re-run on init key, not on every items change
  }, [isOpen, doc_type, documentInitializationKey, expenseAccounts.length]);

  // Seed GL suggestions from the intake-time cache (metadata.ai_suggestions,
  // surfaced as doc.ai_suggestions). The intake pipeline already runs the
  // categorization cascade when the DIN is created, so opening the modal must
  // NOT fire an AI request — otherwise every open looks like the document was
  // never categorized. A fresh AI call only happens when the user clicks
  // Re-analyze (fetchAISuggestions(true)) or switches to per-line mode.
  useEffect(() => {
    if (!isOpen) return;
    const cached = doc?.ai_suggestions;
    if (!cached) {
      setAiDocSuggestion(null);
      setAiPerLineCategorization([]);
      return;
    }

    const docCf = (doc?.custom_fields ?? {}) as Record<string, unknown>;
    const vehicleId = (docCf.vehicle_id as string) || (doc?.vehicle_id as string) || null;
    const vehicleReg = (docCf.vehicle_registration as string) || (doc?.vehicle_registration as string) || null;
    const hasVehicleCtx = Boolean(vehicleId || vehicleReg);

    const cat = cached.categorization;
    const accountCode = String(cat?.account_code || '');
    if (accountCode) {
      setAiDocSuggestion({
        index: 0,
        account_code: accountCode,
        account_name: String(cat?.account_name || ''),
        confidence: Number(cat?.confidence) || 0,
        reasons: Array.isArray(cat?.reasons) ? cat.reasons : [],
        allocation_type: hasVehicleCtx
          ? getPreferredAllocationType(expenseAccounts, accountCode, true)
          : 'overhead',
        inventory_item_match: (cat?.inventory_item_match as InventoryItemMatch | null | undefined) ?? null,
        suggested_customer_id: null,
        suggested_customer_name: null,
        suggested_vehicle_id: hasVehicleCtx ? vehicleId : null,
        suggested_vehicle_name: hasVehicleCtx ? vehicleReg : null,
      });
    } else {
      setAiDocSuggestion(null);
    }

    if (Array.isArray(cached.perLineCategorization)) {
      setAiPerLineCategorization(cached.perLineCategorization as typeof aiPerLineCategorization);
    } else {
      setAiPerLineCategorization([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per document open
  }, [isOpen, documentInitializationKey]);

  // Per-line categorization is only requested in per-line mode. Fetch it on
  // demand when the user switches from single-GL to per-line.
  useEffect(() => {
    if (!isOpen) return;
    if (glAllocationMode !== 'per_line') return;
    if (aiPerLineCategorization.length > 0) return;
    if (items.length === 0) return;
    void fetchAISuggestions(false);
  }, [glAllocationMode, isOpen, items.length, aiPerLineCategorization.length, fetchAISuggestions]);

  // ============================================================================
  // STOCK AVAILABILITY
  // ============================================================================

  const checkStockAvailabilityForItems = useCallback(async () => {
    if (!formData.client_id || items.length === 0) {
      return;
    }

    const isSalesDocument = ['invoice', 'sales_order', 'quote', 'delivery_note'].includes(doc_type);
    if (!isSalesDocument) {
      return;
    }

    setCheckingStock(true);
    try {
      const inventoryItems = items.filter(item =>
        item.item_id && item.track_inventory !== false && item.line_type !== 'header' && item.line_type !== 'comment' && item.line_type !== 'spacer' && item.line_type !== 'subtotal'
      );

      if (inventoryItems.length === 0) {
        setStockAvailability(new Map());
        return;
      }

      const checkItems = inventoryItems.map(item => ({
        work_item_id: String(item.item_id!),
        requested_quantity: item.quantity || 0,
        warehouse_id: formData.warehouse_id ? Number(formData.warehouse_id) : undefined,
      }));

      const availabilityMap = await checkBulkAvailability({
        client_id: formData.client_id,
        items: checkItems,
      });

      setStockAvailability(availabilityMap);
      setShowStockAvailability(true);

      const insufficientItems: Array<{
        work_item_id: string;
        item_name?: string;
        item_code?: string;
        availability: StockAvailabilityCheckType;
      }> = [];

      for (const [workItemId, checks] of availabilityMap.entries()) {
        const item = inventoryItems.find(i => String(i.item_id) === workItemId);
        if (item) {
          const warehouseCheck = checks.find((c: StockAvailabilityCheckType) =>
            c.warehouse_id === (formData.warehouse_id ? Number(formData.warehouse_id) : undefined)
          );
          if (warehouseCheck && !warehouseCheck.can_fulfill) {
            insufficientItems.push({
              work_item_id: workItemId,
              item_name: item.item_name,
              item_code: item.item_code,
              availability: warehouseCheck,
            });
          }
        }
      }

      if (insufficientItems.length > 0) {
        setInsufficientStockItems(insufficientItems);
        setShowBackorderWorkflow(true);
        toast(`${insufficientItems.length} item${insufficientItems.length !== 1 ? 's' : ''} with insufficient stock - backorder option available`, {
          icon: '⚠️',
          style: {
            background: isDarkMode ? '#fbbf24' : '#fbbf24',
            color: '#000',
          },
        });
      }
    } catch (error) {
      console.error('Failed to check stock availability:', error);
      toast.error('Failed to check stock availability');
    } finally {
      setCheckingStock(false);
    }
  }, [formData.client_id, formData.warehouse_id, items, doc_type, checkBulkAvailability, isDarkMode]);

  // Auto-check stock availability when items change for sales documents
  // Use a JSON signature of item_id + quantity to avoid re-checking on unrelated field edits
  const stockCheckSignature = useMemo(
    () => items
      .filter(item => item.item_id && item.track_inventory !== false && item.line_type !== 'header' && item.line_type !== 'comment' && item.line_type !== 'spacer' && item.line_type !== 'subtotal')
      .map(item => `${item.item_id}:${item.quantity}`)
      .join('|'),
    [items],
  );
  useEffect(() => {
    if (isOpen && ['invoice', 'sales_order', 'quote', 'delivery_note'].includes(doc_type) && stockCheckSignature) {
      const timer = setTimeout(() => {
        void checkStockAvailabilityForItems();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [stockCheckSignature, isOpen, doc_type, checkStockAvailabilityForItems]);

  const handleBackorderConfirm = useCallback(async (backorderItems: Array<{
    work_item_id: string;
    backorder_quantity: number;
    expected_date?: string;
  }>) => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      if (!formData.client_id) {
        toast.error('Client ID is required to create a backorder');
        return;
      }

      const response = await fetch('/api/v1/procurement', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'create-backorder',
          client_id: formData.client_id,
          items: backorderItems.map(bi => ({
            work_item_id: bi.work_item_id,
            quantity: bi.backorder_quantity,
            expected_date: bi.expected_date,
          })),
          document_id: doc?.id || formData.id,
          document_type: doc_type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to create backorder: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to create backorder');
      }

      toast.success(`Backorder created successfully${result.data?.backorder_id ? ` (ID: ${result.data.backorder_id})` : ''}`);
      setShowBackorderWorkflow(false);
    } catch (error) {
      console.error('Failed to create backorder:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create backorder');
    }
  }, [formData.client_id, formData.id, doc?.id, doc_type]);

  // ============================================================================
  // CUSTOMER PRICING
  // ============================================================================

  const applyCustomerPricingToItems = useCallback(async () => {
    if (!formData.client_id || !formData.contact_id || items.length === 0) {
      return;
    }

    const isSalesDocument = ['invoice', 'sales_order', 'quote', 'proposal'].includes(doc_type);
    if (!isSalesDocument) {
      return;
    }

    setApplyingCustomerPricing(true);
    try {
      const inventoryItems = items.filter(item =>
        item.item_id && item.line_type !== 'header' && item.line_type !== 'comment' && item.line_type !== 'spacer' && item.line_type !== 'subtotal'
      );

      if (inventoryItems.length === 0) {
        return;
      }

      const lineItems = inventoryItems.map(item => ({
        work_item_id: String(item.item_id!),
        quantity: item.quantity || 0,
        current_price: item.unit_price,
      }));

      const pricingResults = await autoApplyCustomerPricing({
        client_id: formData.client_id,
        contact_id: formData.contact_id,
        lineItems,
      });

      setItemsWithUndo(prevItems => {
        return prevItems.map(item => {
          if (!item.item_id) return item;

          const pricingResult = pricingResults.find(result => result.work_item_id === String(item.item_id));
          if (!pricingResult || pricingResult.source === 'unchanged') {
            return item;
          }

          const newPrice = pricingResult.applied_price;
          const quantity = item.quantity || 0;
          const discountPercentage = item.discount_percentage || 0;
          const lineSubtotal = multiplyMoney(quantity, newPrice);
          const discountAmount = roundMoney(lineSubtotal * (discountPercentage / 100));
          const lineAfterDiscount = roundMoney(lineSubtotal - discountAmount);
          const taxRatePercentage = item.tax_rate || 0;
          let taxAmount: number;
          let lineTotal: number;
          if (item.tax_inclusive && taxRatePercentage > 0) {
            const taxDivisor = 1 + taxRatePercentage / 100;
            taxAmount = roundMoney(lineAfterDiscount - (lineAfterDiscount / taxDivisor));
            // Line total is ex-VAT; tax is added at the document summary.
            lineTotal = roundMoney(lineAfterDiscount - taxAmount);
          } else {
            taxAmount = roundMoney((lineAfterDiscount * taxRatePercentage) / 100);
            // Line total is ex-VAT; tax is added at the document summary.
            lineTotal = roundMoney(lineAfterDiscount);
          }

          return {
            ...item,
            unit_price: newPrice,
            discount_amount: discountAmount,
            tax_amount: taxAmount,
            line_total: lineTotal,
          };
        });
      });

      setCustomerPricingApplied(true);

      const customerPricedCount = pricingResults.filter(r => r.source === 'customer').length;
      if (customerPricedCount > 0) {
        toast.success(`Applied customer-specific pricing to ${customerPricedCount} items`);
      }
    } catch (error) {
      console.error('Failed to apply customer pricing:', error);
      toast.error('Failed to apply customer pricing');
    } finally {
      setApplyingCustomerPricing(false);
    }
  }, [formData.client_id, formData.contact_id, items, doc_type, autoApplyCustomerPricing, setItemsWithUndo]);

  // Auto-apply customer pricing when contact is selected for sales documents.
  // Reset customerPricingApplied when the set of item_ids changes so newly
  // added items also get customer-specific pricing.
  const itemIdsSignature = useMemo(
    () => items.filter(i => i.item_id).map(i => String(i.item_id)).sort().join(','),
    [items],
  );
  useEffect(() => {
    if (itemIdsSignature === customerPricingAppliedItemIdsRef.current) return;
    setCustomerPricingApplied(false);
    customerPricingAppliedItemIdsRef.current = itemIdsSignature;
  }, [itemIdsSignature]);
  useEffect(() => {
    if (isOpen && formData.contact_id && ['invoice', 'sales_order', 'quote', 'proposal'].includes(doc_type) && !customerPricingApplied) {
      const timer = setTimeout(() => {
        void applyCustomerPricingToItems();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [formData.contact_id, isOpen, doc_type, customerPricingApplied, applyCustomerPricingToItems]);

  // ============================================================================
  // BILLABLE ITEMS IMPORT
  // ============================================================================

  const handleBillableItemsSelected = useCallback((selectedItems: SelectedBillableItem[]) => {
    const defaultTaxRate = preferredDefaultTaxRate;
    const taxRatePercentage = parseTaxRateValue(defaultTaxRate?.rate);
    const taxRateId = defaultTaxRate?.id;

    const newLineItems: DocumentLineItem[] = [];
    const newWorkItemIds: string[] = [];
    const newTimeEntryIds: string[] = [];
    let currentIndex = items.length;

    selectedItems.forEach((selection) => {
      const workItem = selection.workItem;

      newWorkItemIds.push(workItem.id);

      if (selection.includeTimeEntries && workItem.time_entries) {
        const selectedTimeEntries = workItem.time_entries.filter(
          te => selection.selectedTimeEntryIds.includes(te.id)
        );

        newTimeEntryIds.push(...selectedTimeEntries.map(te => te.id));

        selectedTimeEntries.forEach(te => {
          const quantity = te.duration_hours;
          const unitPrice = selection.customRate || te.hourly_rate || workItem.hourly_rate || 0;
          const lineSubtotal = quantity * unitPrice;
          const taxAmount = (lineSubtotal * taxRatePercentage) / 100;
          const lineTotal = roundMoney(lineSubtotal);

          newLineItems.push({
            item_id: undefined,
            item_code: workItem.item_number,
            description: `${workItem.title} - ${te.activity_type || 'Labor'}: ${te.description || 'Time Entry'} [${workItem.item_number}]`,
            quantity,
            unit: 'hrs',
            unit_price: unitPrice,
            discount_percentage: 0,
            discount_amount: 0,
            tax_rate: taxRatePercentage,
            tax_rate_id: taxRateId,
            tax_amount: taxAmount,
            line_total: lineTotal,
            sort_order: currentIndex++,
            source_work_item_id: workItem.id,
            source_time_entry_id: te.id,
          });
        });
      }

      if (selection.includeMaterials && workItem.bom_items) {
        const selectedBOMItems = workItem.bom_items.filter(
          bi => selection.selectedBOMItemIds.includes(bi.id)
        );

        selectedBOMItems.forEach(bi => {
          const quantity = bi.quantity;
          const unitPrice = bi.unit_cost;
          const lineSubtotal = quantity * unitPrice;
          const itemTaxRate = bi.tax_rate ?? taxRatePercentage;
          const taxAmount = (lineSubtotal * itemTaxRate) / 100;
          const lineTotal = roundMoney(lineSubtotal);

          newLineItems.push({
            item_id: bi.item_id,
            item_code: bi.item_code || '',
            description: `${bi.item_name}${bi.description ? ` - ${bi.description}` : ''} [${workItem.item_number}]`,
            quantity,
            unit: bi.unit || 'ea',
            unit_price: unitPrice,
            discount_percentage: 0,
            discount_amount: 0,
            tax_rate: itemTaxRate,
            tax_rate_id: taxRateId,
            tax_amount: taxAmount,
            line_total: lineTotal,
            sort_order: currentIndex++,
            source_work_item_id: workItem.id,
          });
        });
      }

      if (selection.includeExpenses && workItem.expenses) {
        const selectedExpenses = workItem.expenses.filter(
          exp => selection.selectedExpenseIds.includes(exp.id)
        );

        const categoryLabel: Record<string, string> = {
          fuel: 'Fuel', toll: 'Toll Fee', parking: 'Parking Fee',
          purchase: 'Job Purchase', maintenance: 'Maintenance',
          breakdown: 'Roadside Assistance', vehicle_expense: 'Vehicle Expense',
        };

        selectedExpenses.forEach(exp => {
          const amount = exp.amount || 0;
          if (amount <= 0) return;

          const lineSubtotal = amount;
          const taxAmount = (lineSubtotal * taxRatePercentage) / 100;
          const lineTotal = roundMoney(lineSubtotal);

          newLineItems.push({
            item_id: undefined,
            item_code: 'EXP-RECHARGE',
            description: `Expense: ${categoryLabel[exp.expense_category || ''] || exp.item_type} - ${exp.title || exp.supplier_name || 'Expense'} [${workItem.item_number}]`,
            quantity: 1,
            unit: 'ea',
            unit_price: amount,
            discount_percentage: 0,
            discount_amount: 0,
            tax_rate: taxRatePercentage,
            tax_rate_id: taxRateId,
            tax_amount: taxAmount,
            line_total: lineTotal,
            sort_order: currentIndex++,
            source_work_item_id: workItem.id,
          });
        });
      }

      if (!selection.includeTimeEntries && !selection.includeMaterials && !selection.includeExpenses) {
        const quantity = 1;
        const unitPrice = selection.customRate || workItem.actual_cost || workItem.estimated_cost || 0;
        const lineSubtotal = roundMoney(quantity * unitPrice);
        const taxAmount = roundMoney((lineSubtotal * taxRatePercentage) / 100);
        const lineTotal = roundMoney(lineSubtotal + taxAmount);

        newLineItems.push({
          item_id: undefined,
          item_code: workItem.item_number,
          description: selection.customDescription || `${workItem.title} [${workItem.item_number}]`,
          quantity,
          unit: 'ea',
          unit_price: unitPrice,
          discount_percentage: 0,
          discount_amount: 0,
          tax_rate: taxRatePercentage,
          tax_rate_id: taxRateId,
          tax_amount: taxAmount,
          line_total: lineTotal,
          sort_order: currentIndex++,
          source_work_item_id: workItem.id,
        });
      }
    });

    setItemsWithUndo(prev => [...prev, ...newLineItems]);
    setImportedWorkItemIds(prev => [...new Set([...prev, ...newWorkItemIds])]);
    setImportedTimeEntryIds(prev => [...new Set([...prev, ...newTimeEntryIds])]);

    const totalAmount = newLineItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
    toast.success(`Imported ${newLineItems.length} item${newLineItems.length !== 1 ? 's' : ''} (${currencySymbol} ${formatCurrency(totalAmount)})`);
  }, [preferredDefaultTaxRate, items.length, currencySymbol, formatCurrency, setItemsWithUndo]);

  // ============================================================================
  // RESET
  // ============================================================================

  const resetLineItemsState = useCallback(() => {
    setItems([]);
    clearUndoStack();
    setShowItemSearch(false);
    setOpenInsertMenu(null);
    setHighlightedInsertIndex(-1);
    setSelectedItemIndices([]);
    setInlineSearchIndex(null);
    setSplitIdx(null);
    setSplitQty(1);
    setStockAvailability(new Map());
    setShowStockAvailability(false);
    setCheckingStock(false);
    setShowBackorderWorkflow(false);
    setInsufficientStockItems([]);
    setCustomerPricingApplied(false);
    setApplyingCustomerPricing(false);
    setAiPerLineCategorization([]);
    setAiDocSuggestion(null);
    setAiSuggestionsLoading(false);
    setImportedWorkItemIds([]);
    setImportedTimeEntryIds([]);
    setLineItemCommentThread({ isOpen: false, lineItemIndex: null });
    setExpandedAllocations(new Set());
    setGlAllocationMode(isExpenseLikeDoc ? 'single' : 'per_line');
  }, [setItems, isExpenseLikeDoc, clearUndoStack]);

  return {
    showItemSearch,
    setShowItemSearch,
    openInsertMenu,
    setOpenInsertMenu,
    highlightedInsertIndex,
    setHighlightedInsertIndex,
    selectedItemIndices,
    setSelectedItemIndices,
    inlineSearchIndex,
    setInlineSearchIndex,
    splitIdx,
    setSplitIdx,
    splitQty,
    setSplitQty,
    stockAvailability,
    showStockAvailability,
    setShowStockAvailability,
    checkingStock,
    showBackorderWorkflow,
    setShowBackorderWorkflow,
    insufficientStockItems,
    customerPricingApplied,
    setCustomerPricingApplied,
    applyingCustomerPricing,
    aiPerLineCategorization,
    aiDocSuggestion,
    aiSuggestionsLoading,
    importedWorkItemIds,
    setImportedWorkItemIds,
    importedTimeEntryIds,
    setImportedTimeEntryIds,
    lineItemCommentThread,
    setLineItemCommentThread,
    preferredDefaultTaxRate,
    handleItemChange,
    handleRemoveItem,
    handleLineItemAccountChange,
    handleLineItemAllocationTypeChange,
    handleSplitLineItem,
    allocationDimensions,
    expandedAllocations,
    setExpandedAllocations,
    getLineItemDimensions,
    getLineItemSplits,
    updateLineItemAllocations,
    handleDragEnd,
    handleItemSelected,
    handleInlineItemSelect,
    handleAddStructuredLine,
    toggleInsertMenu,
    handleInsertMenuAction,
    fetchAISuggestions,
    handleApplyAICategorization,
    handleApplyFullAllocation,
    handleApplySingleGLAllocation,
    glAllocationMode,
    setGlAllocationMode,
    checkStockAvailabilityForItems,
    handleBackorderConfirm,
    applyCustomerPricingToItems,
    handleBillableItemsSelected,
    resetLineItemsState,
    // Undo support
    canUndo,
    undoLastItemChange,
    clearUndoStack,
  };
}
